import { Button, Flex, FormControl, FormErrorMessage, FormLabel, Heading, Input, Stack } from '@chakra-ui/react'
import type { UpdateCompetitionTeamRequest } from '@clsplus/api-types/api-admin'
import { zodResolver } from '@hookform/resolvers/zod'
import type { QueryClient } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import type { LoaderFunctionArgs } from 'react-router-dom'
import { useLoaderData, useNavigate, useParams, useRevalidator } from 'react-router-dom'
import * as z from 'zod'

import { competitionTeamDetailsQuery } from '../../../../api/get-competition-team-details'
import { updateCompetitionTeamDetails } from '../../../../api/update-competition-team-details'

export const loader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) =>
    await queryClient.ensureQueryData(competitionTeamDetailsQuery(params.competitionId || '', params.teamId || ''))

const competitionTeamEditSchema = z.object({
  name: z.string().min(1, { message: 'Please enter a team name' }),
})

type CompetitionTeamEditFormData = z.infer<typeof competitionTeamEditSchema>

export default function CompetitionTeamEdit() {
  const params = useParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const revalidator = useRevalidator()
  const initialData = useLoaderData() as Awaited<ReturnType<ReturnType<typeof loader>>>

  const teamId = params.teamId || ''
  const competitionId = params.competitionId || ''

  const { data: teamDetails } = useQuery({
    ...competitionTeamDetailsQuery(competitionId, teamId),
    initialData,
  })

  const updateTeamMutation = useMutation({
    mutationFn: (formData: UpdateCompetitionTeamRequest) =>
      updateCompetitionTeamDetails(competitionId, teamId, formData),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['competitions'] }),
        queryClient.invalidateQueries({ queryKey: ['competition', competitionId] }),
        queryClient.invalidateQueries({ queryKey: ['team', teamId] }),
      ])
      revalidator.revalidate()
      navigate(`/competitions/${competitionId}/team/${teamId}`)
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<CompetitionTeamEditFormData>({
    defaultValues: {
      name: teamDetails.name ?? null,
    },
    resolver: zodResolver(competitionTeamEditSchema),
  })

  const handleSubmitClick = (formData: CompetitionTeamEditFormData) => {
    updateTeamMutation.mutate(formData)
  }

  return (
    <Flex direction="column" w="100%">
      <Heading as="h2" size="lg">
        {teamDetails.name}
      </Heading>
      <Stack
        as="form"
        noValidate
        id="team-edit"
        spacing={4}
        maxW="container.sm"
        mt={8}
        onSubmit={handleSubmit(handleSubmitClick)}
      >
        <FormControl isInvalid={!!errors.name}>
          <FormLabel>Team name</FormLabel>
          <Input {...register('name')} />
          {errors.name && <FormErrorMessage>{errors.name.message}</FormErrorMessage>}
        </FormControl>
      </Stack>
      <Stack direction="row" spacing={4} mt={8}>
        <Button
          type="submit"
          form="team-edit"
          variant="primary"
          isDisabled={!isDirty}
          isLoading={updateTeamMutation.isLoading}
        >
          Save changes
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate(`/competitions/${competitionId}/team/${teamId}`)}
          isDisabled={updateTeamMutation.isLoading}
        >
          Cancel
        </Button>
      </Stack>
    </Flex>
  )
}
